import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    alpha,
    Box,
    Collapse,
    Link,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink as RouterLink } from "react-router-dom";
import { useSelector } from "src/store";

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  link,
  variant,
  ...rest
}) => {
  const [open, setOpen] = useState(openProp);
  const { t } = useTranslation();
  const { adminMode } = useSelector((state) => state.settings);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const isDense = variant === "dense-pro";
  const isSubMenu = depth > 0;

  let paddingLeft = 1;

  if (isSubMenu) {
    // Subitems: forzamos una sangria significativamente mayor que el inicio
    // del texto del padre (que incluye columna de icono).
    paddingLeft = {
      xs: (isDense ? 5.6 : 6.1) + Math.max(0, depth - 1) * 0.9,
      sm: (isDense ? 5.9 : 6.4) + Math.max(0, depth - 1) * 0.95,
    };
  }

  const itemSx = {
    width: "100%",
    px: isDense ? { xs: 0.65, sm: 0.75 } : { xs: 0.8, sm: 1 },
    py: isDense ? { xs: 0.38, sm: 0.46 } : { xs: 0.52, sm: 0.65 },
    borderRadius: isDense ? 1.4 : 1.8,
    justifyContent: "flex-start",
    textAlign: "left",
    minHeight: isDense ? { xs: 28, sm: 30 } : { xs: 33, sm: 36 },
    pl: paddingLeft,
    color: (theme) =>
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.92)
        : theme.palette.text.primary,
    opacity: 1,
    "&:hover": {
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? alpha(theme.palette.primary.main, 0.2)
          : "#f8fafc",
      color: (theme) =>
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.98)
          : theme.palette.text.primary,
      "& .MuiListItemIcon-root": {
        color: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.95)
            : "#475569",
      },
    },
  };

  const activeSx = {
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? alpha(theme.palette.primary.main, 0.26)
        : "#eef2ff",
    color: (theme) =>
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.98)
        : theme.palette.text.primary,
    fontWeight: 700,
    "& .MuiListItemIcon-root": {
      color: (theme) =>
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.98)
          : "#4f46e5",
    },
  };

  const renderIcon = () => {
    if (!Icon) return null;

    if (React.isValidElement(Icon)) {
      return React.cloneElement(Icon, {
        fontSize: Icon.props.fontSize || "small",
      });
    }

    if (typeof Icon === "function" || typeof Icon === "object") {
      const maybeElement = React.createElement(Icon, {
        fontSize: isDense ? "small" : "medium",
      });
      if (React.isValidElement(maybeElement)) return maybeElement;
    }

    return null;
  };

  if (children) {
    return (
      <ListItem disablePadding key={title} {...rest} sx={{ display: "block" }}>
        <ListItemButton
          onClick={handleToggle}
          sx={itemSx}
          className={className}
        >
          {!isSubMenu && Icon && (
            <ListItemIcon
              sx={{
                minWidth: isDense ? { xs: 24, sm: 26 } : { xs: 27, sm: 30 },
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.86)
                    : "text.secondary",
              }}
            >
              {renderIcon()}
            </ListItemIcon>
          )}
          <ListItemText
            primary={t(title)}
            slotProps={{
              primary: {
                sx: {
                  fontWeight: isDense ? 620 : 650,
                  fontSize: isDense
                    ? { xs: "0.8rem", sm: "0.84rem" }
                    : { xs: "0.86rem", sm: "0.94rem" },
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "inherit",
                },
              },
            }}
          />
          {open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ pt: isDense ? 0.05 : 0.2 }}>{children}</Box>
        </Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding key={title} {...rest} sx={{ display: "block" }}>
      {link ? (
        <Link
          href={href}
          target="_blank"
          underline="none"
          sx={{
            ...itemSx,
            display: "flex",
            alignItems: "center",
            ...(openProp ? activeSx : {}),
          }}
        >
          {!isSubMenu && Icon && (
            <ListItemIcon
              sx={{
                minWidth: isDense ? { xs: 24, sm: 26 } : { xs: 27, sm: 30 },
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.86)
                    : "text.secondary",
              }}
            >
              {renderIcon()}
            </ListItemIcon>
          )}
          <ListItemText
            primary={t(title)}
            slotProps={{
              primary: {
                sx: {
                  fontWeight: isDense ? 560 : 580,
                  fontSize: isDense
                    ? { xs: "0.78rem", sm: "0.82rem" }
                    : { xs: "0.84rem", sm: "0.9rem" },
                },
              },
            }}
          />
          {Info && <Info />}
        </Link>
      ) : (
        <ListItemButton
          component={RouterLink}
          className={className}
          to={href}
          sx={{
            ...itemSx,
            "&.active": activeSx,
          }}
        >
          {!isSubMenu && Icon && (
            <ListItemIcon
              sx={{
                minWidth: isDense ? { xs: 24, sm: 26 } : { xs: 27, sm: 30 },
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.86)
                    : "text.secondary",
              }}
            >
              {renderIcon()}
            </ListItemIcon>
          )}
          <ListItemText
            primary={t(title)}
            slotProps={{
              primary: {
                sx: {
                  fontWeight: isDense ? 560 : 580,
                  fontSize: isDense
                    ? { xs: "0.78rem", sm: "0.82rem" }
                    : { xs: "0.84rem", sm: "0.9rem" },
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              },
            }}
          />
          {Info && <Info />}
        </ListItemButton>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["premium-clean", "dense-pro"]),
};

NavItem.defaultProps = {
  open: false,
  variant: "premium-clean",
};

export default NavItem;
